// @givmo/board — where an officer finds a batch of past records waiting (CBG-AI5-PROPOSE, frames i1,
// m1; backend `board-ai-proposals.md` §6.2).
//
// Two places and the menu's count, no more: the import page carries a card per waiting batch at its
// top; the Minute book, the row the menu lights for that page, carries one line
// above its records. Each goes to the batch's page, where the confirmation is. Both show only to
// someone holding `board_record:import`, the confirmation's own permission (the read is asked for no
// one else, `proposal-queries.ts`), and only while a batch waits; neither repeats what the batch's
// page says.

import { Card, CardBody, saasButtonVariants } from '@givmo/design-system';
import { useBoardHost } from '../../board-host';
import { BOARD_PAGES } from '../../board-routes';
import { Link } from '../../board-routing';
import { displayDate } from '../../lib/display';
import { proposedThrough, type PastRecordBatchSummary } from './proposal-decode';
import { proposalWords as w } from './proposal-words';

const LINE = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const QUIET = 'font-sans text-s12 font-w400 tracking-givmo text-text-tertiary';

function ReviewLink({ batch, testId }: { batch: PastRecordBatchSummary; testId: string }) {
  return (
    <Link
      to={BOARD_PAGES.proposedBatch}
      params={{ batchId: batch.id }}
      // Secondary: the page keeps its own one primary ("Record it", CBG-UX11); the menu's count and
      // this card's place at the top already say it waits.
      className={saasButtonVariants({ size: 'md', tone: 'secondary', className: 'no-underline' })}
      data-testid={testId}
    >
      {w.reviewThem(batch.staged_count)}
    </Link>
  );
}

/** The import page's card for each waiting batch (frame i1). */
export function BatchesWaiting({ batches }: { batches: readonly PastRecordBatchSummary[] }) {
  const { userId } = useBoardHost();
  if (batches.length === 0) return null;
  return (
    <div className="flex flex-col gap-12" data-testid="import-batches-waiting">
      {batches.map((batch) => (
        <Card key={batch.id} testId={`import-batch-waiting-${batch.id}`}>
          <CardBody className="flex flex-col gap-12 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-col gap-4">
              <span className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
                {w.waitingHeading}
              </span>
              <span className={LINE}>
                {w.batchWaiting(batch.staged_count, batch.files_missing_count)}
              </span>
              <span className={QUIET}>
                {w.proposedOn(proposedThrough(batch, userId), displayDate(batch.created_at))}
              </span>
            </div>
            <div className="shrink-0">
              <ReviewLink batch={batch} testId={`import-batch-waiting-review-${batch.id}`} />
            </div>
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

/** The Minute book's one line while a batch waits (frame m1): the first, which the import page lists. */
export function BookWaitingLine({ batches }: { batches: readonly PastRecordBatchSummary[] }) {
  const batch = batches[0];
  if (batch === undefined) return null;
  return (
    <Card testId="records-proposals-waiting">
      <CardBody className="flex flex-col gap-10 sm:flex-row sm:items-center sm:justify-between">
        <span className={LINE}>{w.bookWaiting(batch.staged_count)}</span>
        <div className="shrink-0">
          <ReviewLink batch={batch} testId="records-proposals-review" />
        </div>
      </CardBody>
    </Card>
  );
}
